import { Slide } from '@features/powerpoint';
import { dimensionSlide } from '@features/executive-report/slides/v2/dimension';
import { buildNavbarText } from '@features/executive-report/slides/v2/components/navbar-texts';
import { navbarSections } from '../../domain/navbar-sections';
import { themeV2 } from '@features/executive-report/domain/theme-v2';
import { formatters } from '@utils/formattersV2';
import { formatWithSign } from '@features/executive-report/utils/format-with-sign';
import { visibleSlide } from '@features/executive-report/slides';
import { FormSchema } from '../../form-schema';
import { BuildReportOptionsV2 } from '../../utils/build-slides-v2';

type BuildDimensionSlideProps = {
  navbarSection: (typeof navbarSections)[number];
  dimensionName: string;
  dimensionSlug: string;
  headerPrefix: string;
  dimensions: NonNullable<
    BuildReportOptionsV2['topSalesSubdimensionsByName']['ad']
  >;
  overallMetrics: Parameters<typeof dimensionSlide>[0]['overallMetrics'];
  subdimensionImages: FormSchema['subdimensionImagesByNameAndValue'];
};

const buildDimensionSlide = (props: BuildDimensionSlideProps): Slide => {
  if (props.dimensions.length === 0) return {};

  return dimensionSlide({
    navbarText: buildNavbarText(navbarSections, props.navbarSection),
    dimensionName: props.dimensionName,
    overallMetrics: props.overallMetrics,
    dimensionMetrics: props.dimensions.map(
      (
        subdimension
      ): Parameters<typeof dimensionSlide>[0]['dimensionMetrics'][number] => ({
        dimension: subdimension.value,
        image:
          props.subdimensionImages[props.dimensionSlug]?.[subdimension.value] ??
          null,
        metrics: [
          {
            label: 'Sales Lift',
            value: formatWithSign(
              subdimension.salesMetrics?.incrementalSalesLift.total,
              formatters.lift
            ),
          },
          {
            label: 'Incremental Sales',
            value: formatters.currency(
              subdimension.salesMetrics?.incrementalSales.total,
              { full: true, decimals: 0 }
            ),
          },
          {
            label: 'Impressions',
            value: formatters.impressions(
              subdimension.visitMetrics.impressions,
              { full: true }
            ),
          },
        ],
      })
    ),
    headerText: {
      text: [
        {
          text: `${props.headerPrefix}: `,
          options: {
            color: themeV2.colors.inmarketGreen,
          },
        },
        {
          text: `${props.dimensionName}s`,
        },
      ],
      options: {
        fontSize: 36,
        bold: true,
        h: 0.75,
        w: 12.67,
        x: 0.33,
        y: 0.59,
      },
    },
  });
};

export const salesDimensionSlides = (props: BuildReportOptionsV2): Slide[] => {
  const overallMetrics: Parameters<typeof dimensionSlide>[0]['overallMetrics'] =
    [
      {
        label: 'Incremental Sales and Lift',
        value: formatters.currency(
          props.currentSalesMetrics?.incrementalSales.total ?? 0,
          { decimals: 1 }
        ),
        lift: formatWithSign(
          props.currentSalesMetrics?.incrementalSalesLift.total ?? 0,
          formatters.lift
        ),
      },
      {
        label: 'Transaction Size and Lift',
        value: formatters.currency(
          props.currentSalesMetrics?.exposedTransactionSize.total ?? 0,
          { decimals: 0 }
        ),
        lift: formatWithSign(
          props.currentSalesMetrics?.transactionSizeLift.total ?? 0,
          formatters.lift
        ),
      },
      {
        label: 'Penetration and Lift',
        value: formatters.percentage(
          props.currentSalesMetrics?.exposedPenetration.total ?? 0,
          { decimals: 1 }
        ),
        lift: formatWithSign(
          props.currentSalesMetrics?.penetrationLift.total ?? 0,
          formatters.lift
        ),
      },
      {
        label: 'Transaction Rate and Lift',
        value: formatters.number(
          props.currentSalesMetrics?.exposedTransactionRate.total ?? 0,
          { decimals: 1 }
        ),
        lift: formatWithSign(
          props.currentSalesMetrics?.transactionRateLift.total ?? 0,
          formatters.lift
        ),
      },
    ];

  return [
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The Who',
        dimensionName: 'Audience',
        dimensionSlug: 'aud',
        headerPrefix: 'The WHO',
        dimensions: props.topSalesSubdimensionsByName.aud ?? [],
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
      }),
      visible:
        props.customFields.includedSlides.salesLift.who &&
        (props.topSalesSubdimensionsByName.aud?.length ?? 0) > 0,
    }),
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The What',
        dimensionName: 'Creative',
        dimensionSlug: 'creative',
        headerPrefix: 'The WHAT',
        dimensions: props.topSalesSubdimensionsByName.creative ?? [],
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
      }),
      visible:
        props.customFields.includedSlides.salesLift.what &&
        (props.topSalesSubdimensionsByName.creative?.length ?? 0) > 0,
    }),
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The Where',
        dimensionName: 'Channel',
        dimensionSlug: 'channel',
        headerPrefix: 'The WHERE',
        dimensions: props.topSalesSubdimensionsByName.channel ?? [],
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
      }),
      visible:
        props.customFields.includedSlides.salesLift.where &&
        (props.topSalesSubdimensionsByName.channel?.length ?? 0) > 0,
    }),
  ];
};
